import React, { useEffect } from "react"
import { CollapsableContainer } from "@ciq/shared/components/collapsable"
import { Scrubber } from "@ciq/shared/components/scrubber"
import { Slider } from "@ciq/shared/components/slider"
import { useFilterQueryParams } from "../../hooks/filter_query_params"
import { useAnalyticsContext } from "@ciq/shared/contexts/AnalyticsContext"
import { useCampusFlowSession } from "../../hooks/campus_flow_session"

// 20 threshold intervals: indices 0-19, mapping to threshold values 0, 5, 10, ..., 95
const THRESHOLD_INTERVALS = Array.from({ length: 20 }, (_, i) => i)

/**
 * Returns a human-readable label for the given flow threshold value.
 *
 * @param {number} threshold - Threshold percentile (0-95)
 * @returns {string} Label like "All traffic" or "Top 25%"
 */
function getThresholdLabel(threshold) {
  if (threshold === 0) return "All traffic"
  return `Top ${100 - threshold}%`
}

/**
 * Campus Flow module for the left panel.
 * Non-collapsible section with a toggle to enable/disable campus flow visualization
 * and a threshold scrubber to filter flow routes by percentile.
 *
 * @returns {React.ReactElement} Campus flow module component
 */
export function CampusFlowModule() {
  const { campusFlow, flowThreshold, setFilters } = useFilterQueryParams()
  const { map } = useAnalyticsContext()
  const { startSession, endSession, trackInteraction } =
    useCampusFlowSession(map)

  const handleToggle = (checked) => {
    setFilters({ campusFlow: checked })
  }

  const handleThresholdChange = (newInterval) => {
    setFilters({ flowThreshold: newInterval * 5 })
    trackInteraction("threshold_change")
  }

  // Session lifecycle: start when campusFlow is on, end when off or on unmount.
  // Both startSession and endSession are idempotent, so redundant calls are safe.
  useEffect(() => {
    if (campusFlow) {
      startSession()
    }
    return () => {
      endSession()
    }
  }, [campusFlow, startSession, endSession])

  const header = (
    <div className="campus-flow-module__header">
      <span>Campus Flow</span>
      <Slider checked={campusFlow} onChecked={handleToggle} />
    </div>
  )

  return (
    <CollapsableContainer collapsible={false} header={header}>
      {campusFlow && (
        <div className="campus-flow-module__controls">
          <div className="campus-flow-module__threshold">
            <span className="campus-flow-module__label">Threshold</span>
            <span className="campus-flow-module__value">{getThresholdLabel(flowThreshold)}</span>
          </div>
          <Scrubber
            title=""
            interval={Math.min(19, Math.max(0, Math.floor(flowThreshold / 5)))}
            intervals={THRESHOLD_INTERVALS}
            onChange={handleThresholdChange}
          />
          <div className="campus-flow-module__slider-labels">
            <span>All</span>
            <span>Top 5%</span>
          </div>
        </div>
      )}
    </CollapsableContainer>
  )
}
